import {
  AccountAvatar,
  AccountStatus,
  AccountSettings as AccountSettingsView,
} from "@softmax/product-ux/account";
import type { ComponentProps } from "react";
import type { LinkedAccounts } from "../account.types";
import { useAccountSettings } from "./use-account-settings";
import { linkedAccountControls } from "./linked-account-controls";
import { profileSaveState } from "./profile-save-state";

export function AccountSettings({
  linkedAccounts,
}: {
  readonly linkedAccounts: LinkedAccounts;
}) {
  const editor = useAccountSettings();
  const { query, save, avatar, avatarUncertain, blocked, refreshAccount } =
    editor;

  if (editor.unavailable || !query.data) {
    return (
      <AccountStatus
        state={
          query.error
            ? { status: "error", message: query.error.message }
            : { status: "loading" }
        }
        onRetry={() => void refreshAccount()}
      />
    );
  }

  const profile = editor.draft ?? {
    name: query.data.name ?? "",
    email: query.data.email ?? "",
    institution: query.data.institution ?? "",
    consentServiceUpdates: query.data.consentServiceUpdates,
    consentMarketing: query.data.consentMarketing,
  };
  let avatarFeedback: Pick<
    ComponentProps<typeof AccountAvatar>,
    "error" | "recovery"
  > = {};

  if (avatarUncertain) {
    avatarFeedback = {
      ...avatarFeedback,
      recovery: { refreshing: query.isFetching },
    };

    if (query.error)
      avatarFeedback = {
        ...avatarFeedback,
        recovery: {
          refreshing: query.isFetching,
          message: `Refresh failed: ${query.error.message}`,
        },
      };
  }

  if (avatar.error) {
    avatarFeedback = {
      ...avatarFeedback,
      error: avatarUncertain
        ? "Could not confirm the photo change. Refresh photo before trying again."
        : avatar.error.message,
    };
  }

  let profileFeedback: Pick<
    ComponentProps<typeof AccountSettingsView>,
    "refreshError"
  > = {};

  if (query.error)
    profileFeedback = {
      ...profileFeedback,
      refreshError: `Account refresh failed: ${query.error.message}`,
    };

  const tosVersion = query.data.tosVersion;
  return (
    <AccountSettingsView
      profile={profile}
      avatar={
        <AccountAvatar
          image={query.data.image}
          name={profile.name}
          pending={avatar.isPending || save.isPending}
          disabled={blocked}
          {...avatarFeedback}
          onRefresh={() => void refreshAccount()}
          onFile={editor.changeAvatar}
          onRemove={() => editor.changeAvatar(null)}
        />
      }
      linkedAccounts={linkedAccountControls(linkedAccounts)}
      save={profileSaveState(save)}
      disabled={blocked}
      {...profileFeedback}
      onRetry={() => void refreshAccount()}
      onProfileChange={(changes) =>
        editor.changeProfile({ ...profile, ...changes })
      }
      onSave={() => editor.saveProfile({ profile, tosVersion })}
    />
  );
}
